import { Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { lazy, Suspense } from "react";
import { Provider } from "react-redux";
import { store } from "./store";
import Layout from "./components/Layout";
import ProtectedRoute from "./components/ProtectedRoute";
import { PermissionsProvider } from "./contexts/permissions-context";
import { BackgroundJobsProvider } from "./contexts/BackgroundJobsContext";
import { AuthExpirationHandler } from "./components/AuthExpirationHandler";
import { PermissionDeniedHandler } from "./components/PermissionDeniedHandler";
import "./App.css";
import { Box, CircularProgress } from "@mui/material";
// ✅ LAZY LOADING: Las páginas se cargan solo cuando el usuario navega a ellas
// Esto reduce el bundle inicial de ~2MB a ~500KB
// Páginas públicas (se cargan inmediatamente si es necesario)
const CallbackPage = lazy(() => import("./pages/CallbackPage"));
const Login = lazy(() => import("./pages/Login"));
const AccessDenied = lazy(() => import("./pages/AccessDenied"));
// Dashboard
const Dashboard = lazy(() => import("./pages/Dashboard"));
// Maestros
const MaestrosPage = lazy(() => import("./pages/Maestros/MaestrosPage"));
const CountriesListViewRefactored = lazy(() => import("./components/masters/countries/CountriesListViewRefactored"));
const CarriersListViewRefactored = lazy(() => import("./components/masters/carriers/CarriersListViewRefactored"));
const CurrenciesListViewRefactored = lazy(() => import("./components/masters/currencies/CurrenciesListViewRefactored"));
const EquipmentListViewRefactored = lazy(() => import("./components/masters/equipment/EquipmentListViewRefactored"));
const NomenclatorsListViewRefactored = lazy(() => import("./components/masters/nomenclators/NomenclatorsListViewRefactored"));
const PortsListViewRefactored = lazy(() => import("./components/masters/ports/PortsListViewRefactored"));
const InvoiceConceptsListViewRefactored = lazy(() => import("./components/masters/invoice-concepts/InvoiceConceptsListViewRefactored"));
const VatRulesListViewRefactored = lazy(() => import("./components/masters/vat-rules/VatRulesListViewRefactored"));
const ContactsListViewRefactored = lazy(() => import("./components/masters/contacts/ContactsListViewRefactored"));
const UsersListViewRefactored = lazy(() => import("./components/masters/users/UsersListViewRefactored"));
const TariffConceptsListViewRefactored = lazy(() => import("./components/masters/tariff-concepts/TariffConceptsListViewRefactored"));
const EntityAliasesListViewRefactored = lazy(() => import("./components/masters/entity-aliases/EntityAliasesListViewRefactored"));
const CarrierTreatmentListView = lazy(() => import("./components/masters/carrier-treatment/CarrierTreatmentListView"));
const TerminalAliasesListViewRefactored = lazy(() => import("./components/masters/terminal-aliases/TerminalAliasesListViewRefactored"));
const EquipmentTypeAliasesListViewRefactored = lazy(() => import("./components/masters/equipment-type-aliases/EquipmentTypeAliasesListViewRefactored"));
// Configuración
const ProfilePage = lazy(() => import("./pages/Configuracion/ProfilePage"));
const MyPermissionsPage = lazy(() => import("./pages/Configuracion/MyPermissionsPage"));
const RolesPage = lazy(() => import("./pages/Configuracion/RolesPage"));
const PermissionsTreePage = lazy(() => import("./pages/Configuracion/PermissionsTreePage"));
const ConfiguracionVistasPage = lazy(() => import("./pages/Configuracion/ConfiguracionVistasPage"));
// Ayuda
const AyudaPage = lazy(() => import("./pages/Ayuda/AyudaPage"));
const SoportePage = lazy(() => import("./pages/Ayuda/SoportePage"));
// Contratos y Tarifas
const ContractsListViewRefactored = lazy(() => import("./components/contracts/ContractsListViewRefactored"));
const TariffsListViewRefactored = lazy(() => import("./components/tariffs/TariffsListViewRefactored"));
// Bookings
const BookingsListViewRefactored = lazy(() => import("./components/bookings/bookings-list-view-refactored"));
const ReconciliationHealthPage = lazy(() => import("./components/bookings/reconciliation/ReconciliationHealthPage"));
const ContractAssignmentHealthWorkbench = lazy(() => import("./components/bookings/ContractAssignmentHealthWorkbench"));
// Finanzas - Páginas principales
const InternalInvoicesPage = lazy(() => import("./pages/Finanzas/InternalInvoicesPage"));
// Finanzas - Líneas (vistas principales con row spanning)
const SupplierInvoiceLinesListView = lazy(() => import("./components/finance/supplier-invoices/SupplierInvoiceLinesListView"));
const InternalInvoiceLinesListView = lazy(() => import("./components/finance/internal-invoices/InternalInvoiceLinesListView"));
// Importaciones
const ImportacionesPage = lazy(() => import("./pages/Importaciones/ImportacionesPage"));
const ImportarCargarArchivoPage = lazy(() => import("./pages/ImportarCargarArchivoPage"));
const ImportTypeVerificationPage = lazy(() => import("./pages/Importaciones/ImportTypeVerificationPage"));
// Testing (solo desarrollo)
const Test2View = lazy(() => import("./pages/Test2View"));
// ✅ Componente de loading para Suspense
const PageLoader = () => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "50vh",
    }}
  >
    <CircularProgress size={40} />
  </Box>
);
function App() {
  const auth = useAuth();
  // Mostrar loading mientras se verifica la autenticación
  if (auth.isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress />
          <Box component="p" sx={{ mt: 2, color: "text.secondary" }}>
            Cargando...
          </Box>
        </Box>
      </Box>
    );
  }
  return (
    <Provider store={store}>
    <BackgroundJobsProvider>
    <PermissionsProvider>
    {/* ✅ Manejador de expiración de token - escucha eventos 401 y redirige al login */}
    <AuthExpirationHandler />
    {/* ✅ Manejador de permisos denegados - escucha 403, recarga permisos y redirige si es necesario */}
    <PermissionDeniedHandler />
    <Layout>
      {/* ✅ Suspense envuelve todas las rutas para mostrar loader durante lazy loading */}
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Redirect de "/" a "/dashboard" */}
          <Route index element={<Navigate to="/dashboard" replace />} />
          {/* Rutas públicas */}
          <Route path="auth/callback" element={<CallbackPage />} />
          <Route path="access-denied" element={<AccessDenied />} />
        {/* Rutas privadas */}
        <Route
          path="dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="maestros"
          element={
            <ProtectedRoute>
              <MaestrosPage />
            </ProtectedRoute>
          }
        />
        {/* Maestros - Todas las rutas con soporte para deep link (:id opcional) */}
        <Route
          path="/maestros/paises/:id?"
          element={
            <ProtectedRoute>
              <CountriesListViewRefactored />
            </ProtectedRoute>
          }
        />
        <Route
          path="/maestros/navieras/:id?"
          element={
            <ProtectedRoute>
              <CarriersListViewRefactored />
            </ProtectedRoute>
          }
        />
        <Route
          path="/maestros/monedas/:id?"
          element={
            <ProtectedRoute>
              <CurrenciesListViewRefactored />
            </ProtectedRoute>
          }
        />
        <Route
          path="/maestros/equipos/:id?"
          element={
            <ProtectedRoute>
              <EquipmentListViewRefactored />
            </ProtectedRoute>
          }
        />
        <Route
          path="/maestros/nomencladores/:id?"
          element={
            <ProtectedRoute>
              <NomenclatorsListViewRefactored />
            </ProtectedRoute>
          }
        />
        <Route
          path="/maestros/puertos/:id?"
          element={
            <ProtectedRoute>
              <PortsListViewRefactored />
            </ProtectedRoute>
          }
        />
        <Route
          path="/maestros/conceptos/:id?"
          element={
            <ProtectedRoute>
              <InvoiceConceptsListViewRefactored />
            </ProtectedRoute>
          }
        />
        <Route
          path="/maestros/reglas-iva/:id?"
          element={
            <ProtectedRoute>
              <VatRulesListViewRefactored />
            </ProtectedRoute>
          }
        />
        <Route
          path="/maestros/contactos/:id?"
          element={
            <ProtectedRoute>
              <ContactsListViewRefactored />
            </ProtectedRoute>
          }
        />
        <Route
          path="/maestros/conceptos-tarifa/:id?"
          element={
            <ProtectedRoute>
              <TariffConceptsListViewRefactored />
            </ProtectedRoute>
          }
        />
        <Route
          path="/maestros/mapeo-maestros/:id?"
          element={
            <ProtectedRoute>
              <EntityAliasesListViewRefactored />
            </ProtectedRoute>
          }
        />
        <Route
          path="/maestros/tratamiento-navieras"
          element={
            <ProtectedRoute>
              <CarrierTreatmentListView />
            </ProtectedRoute>
          }
        />
        <Route
          path="/maestros/alias-terminales/:id?"
          element={
            <ProtectedRoute>
              <TerminalAliasesListViewRefactored />
            </ProtectedRoute>
          }
        />
        <Route
          path="/maestros/alias-equipos/:id?"
          element={
            <ProtectedRoute>
              <EquipmentTypeAliasesListViewRefactored />
            </ProtectedRoute>
          }
        />
        {/* Rutas de Configuración - Con soporte para deep link */}
        <Route
          path="/configuracion/usuarios/:id?"
          element={
            <ProtectedRoute>
              <UsersListViewRefactored />
            </ProtectedRoute>
          }
        />
        {/* Rutas de Permisos */}
        <Route
          path="/configuracion/roles"
          element={
            <ProtectedRoute>
              <RolesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/configuracion/permisos"
          element={
            <ProtectedRoute>
              <PermissionsTreePage />
            </ProtectedRoute>
          }
        />
<Route
          path="/configuracion/vistas"
          element={
            <ProtectedRoute>
              <ConfiguracionVistasPage />
            </ProtectedRoute>
          }
        />
        {/* Ruta de Perfil */}
        <Route
          path="/perfil"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/mis-permisos"
          element={
            <ProtectedRoute>
              <MyPermissionsPage />
            </ProtectedRoute>
          }
        />
        {/* Rutas de Ayuda */}
        <Route
          path="/ayuda"
          element={
            <ProtectedRoute>
              <AyudaPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ayuda/soporte"
          element={
            <ProtectedRoute>
              <SoportePage />
            </ProtectedRoute>
          }
        />
        {/* Ruta de Login */}
        <Route path="/login" element={<Login />} />
        {/* Ruta de prueba */}
        <Route
          path="/test2"
          element={
            <ProtectedRoute>
              <Test2View />
            </ProtectedRoute>
          }
        />
        {/* Rutas de Contratos - Con soporte para deep link */}
        <Route
          path="/contratos/:id?"
          element={
            <ProtectedRoute>
              <ContractsListViewRefactored />
            </ProtectedRoute>
          }
        />
        {/* Rutas de Tarifas - Con soporte para deep link */}
        <Route
          path="/tarifas/:id?"
          element={
            <ProtectedRoute>
              <TariffsListViewRefactored />
            </ProtectedRoute>
          }
        />
        {/* Rutas de Bookings - Con soporte para deep link */}
        <Route
          path="/bookings/contract-assignment-health"
          element={
            <ProtectedRoute>
              <ContractAssignmentHealthWorkbench />
            </ProtectedRoute>
          }
        />
        <Route
          path="/bookings/reconciliation-health"
          element={
            <ProtectedRoute>
              <ReconciliationHealthPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/bookings/:id?"
          element={
            <ProtectedRoute>
              <BookingsListViewRefactored />
            </ProtectedRoute>
          }
        />
        {/* Finanzas - Facturas de Proveedores - Con soporte para deep link */}
        <Route
          path="/finanzas/facturas/:id?"
          element={
            <ProtectedRoute>
              <SupplierInvoiceLinesListView />
            </ProtectedRoute>
          }
        />
        {/* Finanzas - Facturas Internas (líneas con info de booking) - Con soporte para deep link */}
        <Route
          path="/finanzas/facturas-internas/:id?"
          element={
            <ProtectedRoute>
              <InternalInvoicesPage />
            </ProtectedRoute>
          }
        />
        {/* Finanzas - Líneas de Facturas Internas (reales) - Con soporte para deep link */}
        <Route
          path="/finanzas/facturas-internas/lineas/:id?"
          element={
            <ProtectedRoute>
              <InternalInvoiceLinesListView />
            </ProtectedRoute>
          }
        />
        {/* Rutas de Importaciones */}
        <Route
          path="/importaciones"
          element={
            <ProtectedRoute>
              <ImportacionesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/importar/cargar-archivo"
          element={
            <ProtectedRoute>
              <ImportarCargarArchivoPage />
            </ProtectedRoute>
          }
        />
        {/* Verificación de importType */}
        <Route
          path="/importar/verificar-tipo"
          element={
            <ProtectedRoute>
              <ImportTypeVerificationPage />
            </ProtectedRoute>
          }
        />
          {/* Ruta 404 */}
          <Route path="*" element={<div>Página no encontrada</div>} />
        </Routes>
      </Suspense>
    </Layout>
    </PermissionsProvider>
    </BackgroundJobsProvider>
    </Provider>
  );
}
export default App;
